<template>
  <ContentWrap>
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="80px"
    >
      <el-form-item label="订单号" prop="orderNo">
        <el-input
          v-model="queryParams.orderNo"
          class="!w-180px"
          clearable
          placeholder="订单号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="类型" prop="type">
        <el-select v-model="queryParams.type" class="!w-140px" clearable placeholder="全部">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.TRADE_ORDER_COMPLAINT_TYPE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="店铺编号" prop="shopId">
        <el-input
          v-model="queryParams.shopId"
          class="!w-140px"
          clearable
          placeholder="店铺 ID"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="用户编号" prop="userId">
        <el-input
          v-model="queryParams.userId"
          class="!w-140px"
          clearable
          placeholder="用户 ID"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="创建时间" prop="createTime">
        <el-date-picker
          v-model="queryParams.createTime"
          class="!w-240px"
          end-placeholder="结束"
          start-placeholder="开始"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
          :default-time="[new Date('1 00:00:00'), new Date('1 23:59:59')]"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon class="mr-5px" icon="ep:search" />搜索</el-button>
        <el-button @click="resetQuery"><Icon class="mr-5px" icon="ep:refresh" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true" :show-overflow-tooltip="true">
      <el-table-column align="center" label="编号" prop="id" width="80" />
      <el-table-column align="center" label="订单号" prop="orderNo" min-width="160" />
      <el-table-column align="center" label="类型" prop="type" width="90">
        <template #default="{ row }">
          <el-tag>{{ getDictLabel(DICT_TYPE.TRADE_ORDER_COMPLAINT_TYPE, row.type) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column align="center" label="店铺" min-width="140">
        <template #default="{ row }">
          <div>{{ row.shopName || '-' }}</div>
          <div v-if="row.shopId" class="text-xs text-gray-400">ID {{ row.shopId }}</div>
        </template>
      </el-table-column>
      <el-table-column align="center" label="用户" min-width="140">
        <template #default="{ row }">
          <div>{{ row.userNickname || '-' }}</div>
          <div class="text-xs text-gray-400">ID {{ row.userId }}</div>
        </template>
      </el-table-column>
      <el-table-column align="left" label="内容" prop="content" min-width="200" show-overflow-tooltip />
      <el-table-column align="center" label="图片" width="80">
        <template #default="{ row }">
          <span>{{ Array.isArray(row.picUrls) ? row.picUrls.length : 0 }}</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="联系方式" prop="contact" width="130" />
      <el-table-column
        align="center"
        label="创建时间"
        prop="createTime"
        :formatter="dateFormatter"
        width="170"
      />
      <el-table-column align="center" label="操作" width="90" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['trade:order-complaint:query']"
            link
            type="primary"
            @click="openDetail(row)"
          >
            详情
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="queryParams.pageNo"
      v-model:limit="queryParams.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <el-drawer v-model="detailVisible" title="投诉详情" size="480px">
    <template v-if="detail">
      <el-descriptions :column="1" border>
        <el-descriptions-item label="编号">{{ detail.id }}</el-descriptions-item>
        <el-descriptions-item label="订单号">{{ detail.orderNo }}</el-descriptions-item>
        <el-descriptions-item label="类型">
          {{ getDictLabel(DICT_TYPE.TRADE_ORDER_COMPLAINT_TYPE, detail.type) }}
        </el-descriptions-item>
        <el-descriptions-item label="店铺">
          {{ detail.shopName || '-' }}
          <span v-if="detail.shopId" class="text-gray-400">（{{ detail.shopId }}）</span>
        </el-descriptions-item>
        <el-descriptions-item label="用户">
          {{ detail.userNickname || '-' }}
          <span class="text-gray-400">（{{ detail.userId }}）</span>
        </el-descriptions-item>
        <el-descriptions-item label="联系方式">{{ detail.contact || '-' }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ detail.createTime }}</el-descriptions-item>
        <el-descriptions-item label="内容">
          <div class="whitespace-pre-wrap leading-6">{{ detail.content }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="凭证">
          <div v-if="detail.picUrls?.length" class="flex flex-wrap gap-8px">
            <el-image
              v-for="(url, idx) in detail.picUrls"
              :key="idx"
              :src="url"
              :preview-src-list="detail.picUrls"
              :initial-index="idx"
              preview-teleported
              fit="cover"
              class="w-72px h-72px rounded"
            />
          </div>
          <span v-else>-</span>
        </el-descriptions-item>
      </el-descriptions>
    </template>
  </el-drawer>
</template>

<script lang="ts" setup>
import { dateFormatter } from '@/utils/formatTime'
import { DICT_TYPE, getDictLabel, getIntDictOptions } from '@/utils/dict'
import * as ComplaintApi from '@/api/mall/trade/complaint'
import type { TradeOrderComplaintVO } from '@/api/mall/trade/complaint'

defineOptions({ name: 'TradeOrderComplaint' })

const loading = ref(false)
const list = ref<TradeOrderComplaintVO[]>([])
const total = ref(0)
const queryFormRef = ref()
const queryParams = reactive({
  pageNo: 1,
  pageSize: 10,
  orderNo: undefined as string | undefined,
  type: undefined as number | undefined,
  shopId: undefined as string | number | undefined,
  userId: undefined as string | number | undefined,
  createTime: undefined as string[] | undefined
})

const detailVisible = ref(false)
const detail = ref<TradeOrderComplaintVO>()

const getList = async () => {
  loading.value = true
  try {
    const params = {
      ...queryParams,
      shopId: queryParams.shopId ? Number(queryParams.shopId) : undefined,
      userId: queryParams.userId ? Number(queryParams.userId) : undefined
    }
    const data = await ComplaintApi.getOrderComplaintPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

const resetQuery = () => {
  queryFormRef.value?.resetFields()
  handleQuery()
}

const openDetail = async (row: TradeOrderComplaintVO) => {
  if (!row.id) return
  detail.value = await ComplaintApi.getOrderComplaint(row.id)
  detailVisible.value = true
}

onMounted(() => {
  getList()
})
</script>
